/**
 * Client-side CSV export for inventory assets.
 * No edge function required — runs entirely in the browser.
 */
import { supabase } from "@/integrations/supabase/client";

interface CsvAsset {
  name: string;
  brand: string | null;
  model: string | null;
  serial_number: string | null;
  upc: string | null;
  isbn: string | null;
  purchase_date: string | null;
  purchase_price: number | null;
  current_estimated_value: number | null;
  warranty_expiry: string | null;
  locations?: { name: string } | null;
}

const CSV_COLUMNS = [
  "Room",
  "Asset Name",
  "Brand/Model",
  "Serial Number",
  "UPC",
  "ISBN",
  "Estimated Value",
  "Purchase Date",
  "Warranty Expiry",
] as const;

function escapeField(value: string | null | undefined): string {
  const str = value ?? "";
  return `"${str.replace(/"/g, '""')}"`;
}

function formatAssetRow(asset: CsvAsset): string[] {
  const brandModel = [asset.brand, asset.model].filter(Boolean).join(" / ") || "";
  const estimatedValue =
    asset.current_estimated_value != null
      ? String(asset.current_estimated_value)
      : asset.purchase_price != null
      ? String(asset.purchase_price)
      : "";

  return [
    asset.locations?.name ?? "",
    asset.name,
    brandModel,
    asset.serial_number ?? "",
    asset.upc ?? "",
    asset.isbn ?? "",
    estimatedValue,
    asset.purchase_date ?? "",
    asset.warranty_expiry ?? "",
  ];
}

/**
 * Fetches all assets for the current user from Supabase, sorts them by room
 * name alphabetically, then triggers a browser download of a CSV file.
 */
export async function exportInventoryCSV(userId: string): Promise<void> {
  const { data, error } = await supabase
    .from("assets")
    .select("name, brand, model, serial_number, upc, isbn, purchase_date, purchase_price, current_estimated_value, warranty_expiry, locations(name)")
    .eq("user_id", userId)
    .eq("status", "active");

  if (error) throw error;

  const assets = (data ?? []) as CsvAsset[];

  // Sort alphabetically by room name, then asset name within room
  assets.sort((a, b) => {
    const roomA = (a.locations?.name ?? "").toLowerCase();
    const roomB = (b.locations?.name ?? "").toLowerCase();
    if (roomA !== roomB) return roomA.localeCompare(roomB);
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  });

  const headerRow = CSV_COLUMNS.map(escapeField).join(",");
  const dataRows = assets.map((asset) =>
    formatAssetRow(asset).map(escapeField).join(",")
  );

  const csv = [headerRow, ...dataRows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const today = new Date().toISOString().slice(0, 10);
  const filename = `manifest-inventory-${today}.csv`;

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
}
